import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import { Person } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { PROMO_RECENT_VISITS_UPDATED_EVENT, getPromoRecentVisits } from '~/components/Promo/PromoRecentVisits';
import { queryParamNames } from '~/components/Promo/hooks/usePromoCodes';
import { promoCodePath } from '~/components/Promo/promoUrls';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import {
  GetPromoCodeProps,
  PromoCategory,
  PromoTag,
  PromoUserRole,
  getPromoMeta,
  promoCategoryOptions,
  promoRoles,
} from '~/services/PromoService';

import { getPromoTagArrays } from '~/utils/promoTags';

import Spinner from '../Spinner';
import UserSearchWidget from '../UserSearchWidget';

interface Props {
  searchQuery: (query: GetPromoCodeProps) => void;
  isLoading: boolean;
  initialFilters?: Partial<GetPromoCodeProps>;
  opts?: Partial<PromoSearchFormOptions>;
}

export type PromoSearchFormOptions = {
  showPromoCodeName: boolean;
  showUserIdInteracted: boolean;
  showOnlyMine: boolean;
  showHideExpired: boolean;
  showHideApproved: boolean;
  showPromoTags: boolean;
  showCaseId: boolean;
  showEmailRestriction: boolean;
  showNoChildPromos: boolean;
  showOnlyChildPromos: boolean;
  showOnlyParentPromos: boolean;
  showPromoCategory: boolean;
  showDev: boolean;
  showExcludeReferralPromos: boolean;
  showSearchAll: boolean;
  showClearAll: boolean;
  showSearchButtonOnOwnRow: boolean;
};

const defaultOptions: PromoSearchFormOptions = {
  showPromoCodeName: true,
  showUserIdInteracted: true,
  showOnlyMine: true,
  showHideExpired: true,
  showHideApproved: true,
  showPromoTags: true,
  showCaseId: true,
  showEmailRestriction: true,
  showNoChildPromos: true,
  showOnlyChildPromos: true,
  showOnlyParentPromos: true,
  showPromoCategory: true,
  showDev: true,
  showExcludeReferralPromos: true,
  showSearchAll: true,
  showClearAll: true,
  showSearchButtonOnOwnRow: false,
};

type PromoHierarchyFilter = 'child' | 'parent';
type PromoHierarchyFilterOption = PromoHierarchyFilter | 'hide-child';

const promoHierarchyFilterOptions: ReadonlyArray<{ label: string; value: PromoHierarchyFilterOption }> = [
  { label: 'Hide child promos', value: 'hide-child' },
  { label: 'Only Child promos', value: 'child' },
  { label: 'Only Parent promos', value: 'parent' },
];

function PromoSearchForm({ searchQuery, isLoading, initialFilters, opts }: Props) {
  const options = { ...defaultOptions, ...opts };
  const visiblePromoHierarchyFilterOptions = promoHierarchyFilterOptions.filter(
    (option) =>
      (option.value !== 'hide-child' || options.showNoChildPromos) &&
      (option.value !== 'child' || options.showOnlyChildPromos) &&
      (option.value !== 'parent' || options.showOnlyParentPromos),
  );
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const { user: currentUser } = useCurrentUser();

  const [promoCodeName, setPromoCodeName] = useState(initialFilters?.codeName ?? query.get(queryParamNames.name));
  const [recentPromoCodeNames, setRecentPromoCodeNames] = useState<Array<string>>(() =>
    getPromoRecentVisits('promo').map(({ code }) => code),
  );
  const [caseId, setCaseId] = useState(initialFilters?.caseId ?? query.get(queryParamNames.caseId));

  const [userIdInteracted, setUserIdInteracted] = useState(
    initialFilters?.userIdInteracted ?? query.get(queryParamNames.userIdInteracted),
  );
  const [userSearchValue, setUserSearchValue] = useState<string>('');

  const [promoEmailFilter, setPromoEmailFilter] = useState(
    initialFilters?.emailAddress ?? query.get(queryParamNames.emailAddress),
  );
  const [expiresAfterToday, setExpiresAfterToday] = useState<boolean>(
    initialFilters?.expiresAfter !== undefined
      ? Boolean(initialFilters.expiresAfter)
      : query.get(queryParamNames.expiresAfter) != 'false',
  );
  const [noChildPromos, setNoChildPromos] = useState<boolean>(
    initialFilters?.noChildPromos ?? query.get(queryParamNames.noChildPromos) == 'true',
  );
  const [promoHierarchyFilters, setPromoHierarchyFilters] = useState<Array<PromoHierarchyFilter>>(() => {
    const selectedFilters: Array<PromoHierarchyFilter> = [];
    if (initialFilters?.onlyChildPromos ?? query.get(queryParamNames.onlyChildPromos) == 'true') {
      selectedFilters.push('child');
    }
    if (initialFilters?.onlyParentPromos ?? query.get(queryParamNames.onlyParentPromos) == 'true') {
      selectedFilters.push('parent');
    }
    return selectedFilters;
  });
  const onlyChildPromos = promoHierarchyFilters.includes('child');
  const onlyParentPromos = promoHierarchyFilters.includes('parent');
  const [promoCategory, setPromoCategory] = useState<PromoCategory | null>(initialFilters?.promoCategory ?? null);
  const [noReferralPromos, setNoReferralPromos] = useState<boolean>(
    initialFilters?.excludeReferralPromos ?? query.get(queryParamNames.excludeReferralPromos) == 'true',
  );
  const [excludeApprovedPromos, setExcludeApprovedPromos] = useState<boolean>();

  const [promoTagsLoading, setPromoTagsLoading] = useState<boolean>(true);
  const [promoTags, setPromoTags] = useState<Array<PromoTag>>(null);
  const [promoDeptTagOptions, setPromoDeptTagOptions] = useState<Array<string>>([]);
  const [promoDeptTag, setPromoDeptTag] = useState<string>(
    initialFilters?.deptTag ?? query.get(queryParamNames.deptTag),
  );
  const [promoCategoryTagOptions, setPromoCategoryTagOptions] = useState<Array<string>>([]);
  const [promoCategoryTag, setPromoCategoryTag] = useState<string>(
    initialFilters?.categoryTag ?? query.get(queryParamNames.categoryTag),
  );
  const [promoSubCategoryTagOptions, setPromoSubCategoryTagOptions] = useState<Array<string>>([]);
  const [promoSubCategoryTag, setPromoSubCategoryTag] = useState<string>(
    initialFilters?.subCategoryTag ?? query.get(queryParamNames.subCategoryTag),
  );

  const [promoUserRoles, setPromoUserRoles] = useState<Array<PromoUserRole>>([]);
  const [isDev, setIsDev] = useState<boolean>(initialFilters?.dev ?? query.get(queryParamNames.dev) == 'true');

  // Add "Only mine" toggle state
  const [onlyMine, setOnlyMine] = useState<boolean>(false);

  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    Boolean(promoDeptTag) ||
      Boolean(promoCategoryTag) ||
      Boolean(promoSubCategoryTag) ||
      Boolean(noChildPromos) ||
      Boolean(onlyChildPromos) ||
      Boolean(onlyParentPromos) ||
      Boolean(promoCategory) ||
      Boolean(noReferralPromos) ||
      Boolean(caseId) ||
      Boolean(userIdInteracted),
  );

  const { push } = useAppNavigation();

  const getSearchQuery = (): GetPromoCodeProps => {
    // Set userIdInteracted to current user's ID if "Only mine" is enabled
    const finalUserIdInteracted = onlyMine ? currentUser?.memberId || '' : userIdInteracted || '';

    const query: GetPromoCodeProps = {
      codeName: promoCodeName || '',
      page: 1,
      limit: 10,
      brand: brand,
      caseId: caseId || '',
      userIdInteracted: finalUserIdInteracted,
      emailAddress: promoEmailFilter || '',
      expiresAfter: expiresAfterToday ? new Date().toISOString() : '',
      noChildPromos: !!noChildPromos,
      onlyChildPromos: !!onlyChildPromos,
      onlyParentPromos: !!onlyParentPromos,
      promoCategory: promoCategory ?? undefined,
      excludeReferralPromos: noReferralPromos,
      excludeApprovedPromos: excludeApprovedPromos,
      deptTag: promoDeptTag || '',
      categoryTag: promoCategoryTag || '',
      subCategoryTag: promoSubCategoryTag || '',
      dev: isDev,
    };
    return query;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    searchQuery(getSearchQuery());
  };

  const onChange = (setState: Dispatch<SetStateAction<string>>) => (event: ChangeEvent<HTMLInputElement>) => {
    setState(event.target.value);
  };

  const handleUserIdInteractedChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setUserIdInteracted(newValue);
    if (onlyMine) {
      setOnlyMine(false);
    }
  };

  const handleUserSearchSelect = (id: string, userId: string) => {
    setUserIdInteracted(userId);
    if (onlyMine) {
      setOnlyMine(false);
    }
  };

  const handleClearAll = (event) => {
    event.preventDefault();
    setPromoCodeName('');
    setPromoEmailFilter('');
    setCaseId('');
    setExpiresAfterToday(true);
    setNoChildPromos(false);
    setPromoHierarchyFilters([]);
    setPromoCategory(null);
    setNoReferralPromos(false);
    setPromoDeptTag('');
    setPromoCategoryTag('');
    setPromoSubCategoryTag('');
    setIsDev(false);
    setOnlyMine(false);
    setUserIdInteracted('');
    setUserSearchValue('');
    push({ search: '' });
  };

  // Handle "Only mine" toggle change
  const handleOnlyMineChange = () => {
    const newOnlyMineState = !onlyMine;
    setOnlyMine(newOnlyMineState);

    if (newOnlyMineState) {
      // Set userIdInteracted to current user's ID and trigger search
      const currentUserId = currentUser?.memberId || '';
      setUserIdInteracted(currentUserId);
      setUserSearchValue(currentUser?.email || '');

      // Trigger search with the new filter
      const query: GetPromoCodeProps = {
        ...getSearchQuery(),
        userIdInteracted: currentUserId,
      };
      searchQuery(query);
    } else {
      // Clear userIdInteracted when "Only mine" is disabled
      setUserIdInteracted('');
      setUserSearchValue('');

      // Trigger search with cleared userIdInteracted
      const query: GetPromoCodeProps = {
        ...getSearchQuery(),
        userIdInteracted: '',
      };
      searchQuery(query);
    }
  };

  const handleGoToPromo = () => {
    const codeName = promoCodeName?.trim();
    if (!codeName) {
      enqueueSnackbar('Please enter a promo code name');
      return;
    }
    push(promoCodePath(codeName));
  };

  const refreshMetaOptions = (promoTags: Array<PromoTag>, selectedDepartment: string, selectedCategory: string) => {
    const tagArrays = getPromoTagArrays({ promoTags, selectedDepartment, selectedCategory, usersPromoRoles: null });
    setPromoDeptTagOptions(tagArrays.departmentTags);
    setPromoCategoryTagOptions(tagArrays.categoryTags);
    setPromoSubCategoryTagOptions(tagArrays.subCategoryTags);
    if (tagArrays.categoryTags.length == 1) {
      setPromoCategoryTag(tagArrays.categoryTags[0]);
    } else if (tagArrays.categoryTags.length == 0) {
      setPromoCategoryTag('');
    }
    if (tagArrays.subCategoryTags.length == 1) {
      setPromoSubCategoryTag(tagArrays.subCategoryTags[0]);
    } else if (tagArrays.subCategoryTags.length == 0) {
      setPromoSubCategoryTag('');
    }
  };

  useEffect(() => {
    if (!options.showPromoTags) {
      return;
    }
    if (promoTags !== null) {
      return;
    }
    setPromoTagsLoading(true);
    getPromoMeta()
      .then((res) => {
        setPromoTags(res.result.promo_tags);
        setPromoUserRoles(res.result.promo_roles_for_user);
      })
      .catch((e) => {
        enqueueSnackbar(e.message);
      })
      .finally(() => {
        setPromoTagsLoading(false);
      });
  }, [promoTags, enqueueSnackbar, options.showPromoTags]);

  const searchAll = () => {
    setPromoCodeName('ALL');
    handleSubmit(new Event('submit', { cancelable: true }));
  };

  useEffect(() => {
    if (!options.showPromoTags) {
      return;
    }
    if (promoTags != null) {
      refreshMetaOptions(promoTags, promoDeptTag, promoCategoryTag);
    }
  }, [options.showPromoTags, promoTags, promoDeptTag, promoCategoryTag]);

  // Effect to uncheck "Only mine" when userIdInteracted changes from external sources
  useEffect(() => {
    const currentUserId = currentUser?.memberId || '';
    if (onlyMine && userIdInteracted !== currentUserId) {
      setOnlyMine(false);
    }
  }, [userIdInteracted, onlyMine, currentUser?.memberId]);

  useEffect(() => {
    const syncRecentPromoCodeNames = () => {
      setRecentPromoCodeNames(getPromoRecentVisits('promo').map(({ code }) => code));
    };
    window.addEventListener(PROMO_RECENT_VISITS_UPDATED_EVENT, syncRecentPromoCodeNames);
    return () => window.removeEventListener(PROMO_RECENT_VISITS_UPDATED_EVENT, syncRecentPromoCodeNames);
  }, []);

  const isSearching =
    isLoading &&
    (promoCodeName !== '' ||
      userIdInteracted !== '' ||
      caseId !== '' ||
      promoEmailFilter !== '' ||
      noChildPromos ||
      onlyChildPromos ||
      onlyParentPromos ||
      promoCategory !== null ||
      onlyMine);
  const isClearable =
    promoCodeName ||
    userIdInteracted ||
    caseId ||
    promoEmailFilter ||
    expiresAfterToday ||
    noChildPromos ||
    onlyChildPromos ||
    onlyParentPromos ||
    promoCategory ||
    promoDeptTag ||
    promoCategoryTag ||
    promoSubCategoryTag ||
    excludeApprovedPromos ||
    onlyMine;

  return (
    <form onSubmit={handleSubmit}>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={2} mt={2}>
          {options.showPromoCodeName && (
            <>
              <Autocomplete
                freeSolo
                options={recentPromoCodeNames}
                groupBy={() => 'recent-visits'}
                inputValue={promoCodeName ?? ''}
                onInputChange={(_, value) => setPromoCodeName(value)}
                onOpen={() => setRecentPromoCodeNames(getPromoRecentVisits('promo').map(({ code }) => code))}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Typography component="div" variant="caption" color="text.secondary" sx={{ px: 2, pt: 1 }}>
                      Recently visited
                    </Typography>
                    <Box component="ul" sx={{ m: 0, p: 0 }}>
                      {params.children}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Promo code name" placeholder="Promo code name" fullWidth />
                )}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleGoToPromo}
                className="T-promo-search-button"
                type="button"
                disabled={isSearching || !promoCodeName || promoCodeName === 'ALL'}
              >
                Show
              </Button>
            </>
          )}
          {!options.showSearchButtonOnOwnRow && (
            <Button variant="contained" className="T-promo-search-button" type="submit" disabled={isSearching}>
              {isSearching ? 'Searching…' : 'Search'}
            </Button>
          )}
        </Stack>
        {options.showUserIdInteracted && (
          <Stack direction="row" spacing={2} mt={2}>
            <Box sx={{ flex: 1 }}>
              <UserSearchWidget
                id="UserIdInteracted"
                label="Filter by User ID Interacted"
                formErrors={[]}
                loading={isSearching}
                onChange={handleUserSearchSelect}
                passedAutocompleteValue={userSearchValue}
                setPassedAutocompleteValue={setUserSearchValue}
              />
            </Box>
            {options.showOnlyMine && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  onClick={handleOnlyMineChange}
                  disabled={isSearching}
                  color={onlyMine ? 'primary' : 'default'}
                  title="Show only promo codes that you have interacted with (requested, used, logged, or created)"
                  size="small"
                >
                  <Person />
                </IconButton>
              </Box>
            )}
          </Stack>
        )}
        {options.showPromoTags && (
          <Stack direction="row" mt={2}>
            {promoTagsLoading ? <Spinner /> : null}
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                value={promoDeptTag}
                options={promoDeptTagOptions}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Department" />}
                onChange={(e: unknown, newValue: string | null) => {
                  setPromoDeptTag(newValue);
                  setPromoCategoryTag(null);
                  setPromoSubCategoryTag(null);
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                value={promoCategoryTag}
                options={promoCategoryTagOptions}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Category" />}
                onChange={(e: unknown, newValue: string | null) => {
                  setPromoCategoryTag(newValue);
                  setPromoSubCategoryTag(null);
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                value={promoSubCategoryTag}
                options={promoSubCategoryTagOptions}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Sub Category" />}
                onChange={(e: unknown, newValue: string | null) => {
                  setPromoSubCategoryTag(newValue);
                }}
              />
            </Box>
          </Stack>
        )}
        {(options.showCaseId ||
          options.showEmailRestriction ||
          options.showNoChildPromos ||
          options.showOnlyChildPromos ||
          options.showOnlyParentPromos ||
          options.showPromoCategory ||
          options.showDev ||
          options.showExcludeReferralPromos ||
          options.showHideExpired ||
          options.showHideApproved ||
          options.showSearchAll ||
          options.showClearAll) && (
          <Box>
            <Button variant="text" onClick={() => setShowAdvanced(!showAdvanced)}>
              Advanced Options
            </Button>
          </Box>
        )}
        {showAdvanced && (
          <Stack direction="column">
            <Stack direction="row" spacing={2}>
              {options.showCaseId && (
                <TextField
                  id="CaseIdSearch"
                  label="Case Id"
                  placeholder="Case Id"
                  value={caseId}
                  onChange={onChange(setCaseId)}
                  disabled={isSearching}
                  fullWidth
                />
              )}
              {options.showEmailRestriction && (
                <TextField
                  label="Promo Email Restriction"
                  placeholder="Filter by email address restriction"
                  title="Filter by email address restriction (note: does not currently include promo codes used by a specific user, only promo codes that are restricted to a specific email address)"
                  value={promoEmailFilter}
                  onChange={onChange(setPromoEmailFilter)}
                  fullWidth
                />
              )}
              {(options.showCaseId || options.showEmailRestriction) && <DummyButton isSearching={isSearching} />}
            </Stack>
            <Stack direction="row" spacing={2} mt={2}>
              {options.showPromoCategory && (
                <Box sx={{ flex: 1 }}>
                  <Autocomplete
                    value={promoCategoryOptions.find((option) => option.value === promoCategory) ?? null}
                    options={promoCategoryOptions}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => <TextField {...params} label="Promo category" />}
                    onChange={(_, option) => setPromoCategory(option?.value ?? null)}
                  />
                </Box>
              )}
              {options.showHideExpired && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!expiresAfterToday}
                      onChange={onChange(() => setExpiresAfterToday(!expiresAfterToday))}
                    />
                  }
                  label="Hide Expired"
                  title="Only return Promo Codes that have not expired"
                />
              )}
              {options.showHideApproved &&
                promoUserRoles.map((pr) => pr.roleName).includes(promoRoles.PROMO_BETA_TESTER_ROLE) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={excludeApprovedPromos}
                        onChange={onChange(() => setExcludeApprovedPromos(!excludeApprovedPromos))}
                      />
                    }
                    label="Hide Approved"
                    title="Only return Promo Codes that have not been approved"
                  />
                )}
              {(options.showNoChildPromos || options.showOnlyChildPromos || options.showOnlyParentPromos) && (
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Autocomplete
                    multiple
                    value={visiblePromoHierarchyFilterOptions.filter((option) =>
                      option.value === 'hide-child' ? noChildPromos : promoHierarchyFilters.includes(option.value),
                    )}
                    options={visiblePromoHierarchyFilterOptions}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => <TextField {...params} label="Promo hierarchy" />}
                    onChange={(_, selectedOptions) => {
                      const selectedFilters = selectedOptions
                        .map((option) => option.value)
                        .filter((value): value is PromoHierarchyFilter => value !== 'hide-child');
                      setPromoHierarchyFilters(selectedFilters);
                      setNoChildPromos(
                        selectedOptions.some((option) => option.value === 'hide-child') &&
                          !selectedFilters.includes('child'),
                      );
                    }}
                  />
                </Box>
              )}
              {options.showDev && (
                <FormControlLabel
                  control={<Checkbox checked={!!isDev} onChange={onChange(() => setIsDev(!isDev))} />}
                  label="Is Dev"
                  title="Check this box to show dev fields and features"
                />
              )}
              {options.showExcludeReferralPromos && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!noReferralPromos}
                      onChange={onChange(() => setNoReferralPromos(!noReferralPromos))}
                    />
                  }
                  label="Exclude Referral Promo"
                  title="Check this box to hide Promo Codes that are the result of a referral event"
                />
              )}
            </Stack>
            <Stack direction="row" spacing={2} mt={2}>
              <Stack direction="row" spacing={2}>
                {options.showSearchAll && (
                  <Button
                    variant="contained"
                    className="T-promo-search-button"
                    disabled={isSearching}
                    onClick={searchAll}
                  >
                    Search all
                  </Button>
                )}
                {options.showClearAll && isClearable && (
                  <Button variant="text" onClick={handleClearAll} disabled={isLoading}>
                    Clear All
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        )}
        {options.showSearchButtonOnOwnRow && (
          <Box>
            <Button variant="contained" className="T-promo-search-button" type="submit" disabled={isSearching}>
              {isSearching ? 'Searching…' : 'Search'}
            </Button>
          </Box>
        )}
      </Stack>
    </form>
  );
}

function DummyButton({ isSearching }) {
  return (
    <Button variant="contained" className="T-promo-search-button" style={{ visibility: 'hidden' }}>
      {isSearching ? 'Searching…' : 'Search'}
    </Button>
  );
}

export default PromoSearchForm;
